import {
  CONTROL_EVENT_BOOTSTRAP,
  CONTROL_EVENT_HEALTH_ACK,
  CONTROL_EVENT_HEALTH_PROBE,
} from "../constants/chatControlEvents";
import {
  MESSAGE_TYPE_BOOTSTRAP_ACK,
  MESSAGE_TYPE_BOOTSTRAP_ERROR,
  MESSAGE_TYPE_CHAT_RESPONSE,
  MESSAGE_TYPE_CHAT_RESPONSE_CHUNK,
  MESSAGE_TYPE_FACTS_STREAM_ITEM,
  MESSAGE_TYPE_RESULTS_PAYLOAD,
  MESSAGE_TYPE_RESULTS_PAYLOAD_CHUNK,
  MESSAGE_TYPE_WORKFLOW_STATUS,
} from "../constants/messageTypes";
import { notifyHealthAck } from "../directLineHealth";
import { markInitialized, setProjectId, updateProjectMetadata } from "../ProjectSlice_Store";
import { setBootstrapError, unlockChatInput } from "./chatSlice";
import { handledActivityIds } from "./chatModuleState";
import { handleNotification } from "./chatNotifications";
import {
  handleFactsStreamItem,
  handleResultsPayload,
  handleResultsPayloadChunk,
} from "./chatResultsHandlers";
import {
  handleChatResponse,
  handleChatResponseChunk,
  handleWorkflowStatus,
} from "./chatResponseHandlers";
import { handleLegacyActivity } from "./chatLegacyHandler";
import type { ActivityContext, ChatDispatch } from "./chatSliceTypes";

const FEEDBACK_ECHO_PREFIX = "CONTROL_FEEDBACK:";

/**
 * Drop everything that isn't a fresh bot message: non-messages, our own echoed
 * activities, and activity IDs we've already processed.
 */
export function shouldIgnoreActivity(activity: any, directLineUserId?: string): boolean {
  if (!activity || activity.type !== "message") return true;

  const fromId = activity.from?.id;
  if (!fromId) return true;

  // DirectLine echoes user messages back with the same user ID
  if (directLineUserId && fromId === directLineUserId) return true;

  if ((activity.channelData || {}).message_type === "user") return true;

  if (activity.id && handledActivityIds.has(activity.id)) return true;
  if (activity.id) {
    handledActivityIds.add(activity.id);
  }

  return false;
}

/**
 * Control traffic that must never reach the transcript: our own bootstrap and
 * health-probe echoes, the backend's health ack, and feedback echoes.
 * Any other control message falls through to regular handling.
 */
export function isHandledControlMessage(channelData: any, activityText?: string): boolean {
  if (channelData.message_type !== "control") return false;

  const controlEvent = channelData.control_event;
  if (controlEvent === CONTROL_EVENT_BOOTSTRAP || controlEvent === CONTROL_EVENT_HEALTH_PROBE) {
    return true;
  }

  if (controlEvent === CONTROL_EVENT_HEALTH_ACK) {
    // Health probe acknowledgement — notify the health probe helper
    notifyHealthAck();
    return true;
  }

  return Boolean(activityText && activityText.startsWith(FEEDBACK_ECHO_PREFIX));
}

/**
 * Adopt the backend's project ID only for a genuinely new project. When
 * rehydrating, the ID from the URL wins — bootstrap must not overwrite it.
 */
function handleBootstrapAck(channelData: any, dispatch: ChatDispatch): void {
  const payload = channelData.payload || {};
  const existingProjectId = window.store?.getState()?.projectSlice?.currentProjectId;

  if (!existingProjectId || existingProjectId.startsWith("temp-")) {
    console.log("✅ [BOOTSTRAP ACK] Setting project ID:", payload.projectId);
    dispatch(setProjectId(payload.projectId));
  } else if (payload.projectId && payload.projectId !== existingProjectId) {
    console.warn("⚠️ [BOOTSTRAP ACK] Backend returned different project ID than expected:", {
      expected: existingProjectId,
      received: payload.projectId,
    });
  }

  dispatch(markInitialized());

  if (payload.name || payload.summary) {
    dispatch(updateProjectMetadata({ name: payload.name, summary: payload.summary }));
  }
}

type MessageHandler = (channelData: any, activityText: string, ctx: ActivityContext) => void;

/**
 * The message-type taxonomy: switch on `channelData.message_type` rather than
 * parsing JSON out of the activity text.
 */
const MESSAGE_HANDLERS = new Map<string, MessageHandler>([
  [
    MESSAGE_TYPE_BOOTSTRAP_ACK,
    (channelData, _text, ctx) => handleBootstrapAck(channelData, ctx.dispatch),
  ],
  [
    MESSAGE_TYPE_BOOTSTRAP_ERROR,
    (channelData, _text, ctx) => {
      ctx.dispatch(setBootstrapError(channelData.payload?.error || "Unknown bootstrap error"));
      ctx.dispatch(unlockChatInput());
    },
  ],
  [
    MESSAGE_TYPE_RESULTS_PAYLOAD_CHUNK,
    (channelData, _text, ctx) => handleResultsPayloadChunk(channelData, ctx.dispatch),
  ],
  [
    MESSAGE_TYPE_RESULTS_PAYLOAD,
    (channelData, _text, ctx) => handleResultsPayload(channelData, ctx.dispatch),
  ],
  [
    MESSAGE_TYPE_FACTS_STREAM_ITEM,
    (channelData, _text, ctx) => handleFactsStreamItem(channelData, ctx.dispatch),
  ],
  [MESSAGE_TYPE_CHAT_RESPONSE_CHUNK, handleChatResponseChunk],
  [MESSAGE_TYPE_CHAT_RESPONSE, handleChatResponse],
  [MESSAGE_TYPE_WORKFLOW_STATUS, (channelData, _text, ctx) => handleWorkflowStatus(channelData, ctx)],
]);

/** Route one DirectLine activity to whichever handler owns its message type. */
export function handleChatActivity(
  activity: any,
  directLineUserId: string | undefined,
  ctx: ActivityContext
): void {
  if (shouldIgnoreActivity(activity, directLineUserId)) return;

  const channelData = activity.channelData || {};
  const activityText: string = activity.text;

  if (handleNotification(channelData.notification, ctx.dispatch)) return;
  if (isHandledControlMessage(channelData, activityText)) return;

  const handler = MESSAGE_HANDLERS.get(channelData.message_type);
  if (handler) {
    handler(channelData, activityText, ctx);
    return;
  }

  handleLegacyActivity(activityText, ctx.dispatch);
}
